import { createApp } from 'vue';
import { tippy } from 'vue-tippy';
import type { Instance as TippyInstance } from 'tippy.js';
import TopExchangesTippy from '@/js/components/Tippy/TopExchangesTippy/TopExchangesTippy.vue';

interface VueMountedElement extends HTMLElement {
    vueApp?: ReturnType<typeof createApp>;
}

type TippyInstanceList = TippyInstance[];

let tippyInstances: TippyInstanceList = [];

/**
 * Mount all <top-exchanges-tippy> components by locating their parent wrapper elements.
 * Handles Vue app mounting and cleanup.
 */
function mountVueTippies(): void {
    document.querySelectorAll('top-exchanges-tippy').forEach((el) => {
        const wrapper = el.parentElement as VueMountedElement;
        if (!wrapper) return;

        if (wrapper.vueApp) {
            wrapper.vueApp.unmount();
            wrapper.vueApp = null;
        }

        const app = createApp({});
        app.component('TopExchangesTippy', TopExchangesTippy);
        app.mount(wrapper);
        wrapper.vueApp = app;
    });
}

/**
 * Initialize all Tippy.js-based tooltips with content templates.
 */
function initializeTippyTooltips(): void {
    tippyInstances.forEach((instance) => instance.destroy());

    tippyInstances = tippy('.tippy-table', {
        theme: 'tip-info',
        placement: 'bottom',
        interactive: true,
        appendTo: () => document.querySelector('.tooltip-append') || document.body,
    });

    tippyInstances.forEach((instance) => {
        const templateId = instance.reference.getAttribute('data-template');
        const template = templateId ? document.getElementById(templateId) : null;
        if (template) {
            instance.setContent(template);
        }
    });
}

/**
 * Fully (re)initialize all tooltips: Vue and Tippy.js.
 */
function reinitializeAllTooltips(): void {
    requestAnimationFrame(() => {
        initializeTippyTooltips();
        mountVueTippies();
    });
}

/**
 * Set up Livewire integration to reinitialize tooltips on DOM changes.
 */
export function setupLivewireTooltips(): void {
    reinitializeAllTooltips();

    if (typeof window.Livewire !== 'undefined') {
        window.Livewire.on('tableChanged', () => {
            reinitializeAllTooltips();
        });
    }
}

setupLivewireTooltips();
